/*
	This is main store for the application
 */

import * as types from "../mutation-types";
import ServerDataProvider from "../../providers/ServerDataProvider";
import FeatureToggleProvider from "../../providers/FeatureToggleProvider";
import ServerLogProvider from "../../providers/ServerLogProvider";
import ProductCatalogProvider from "../../providers/ProductCatalogProvider";
import appConfig from "../../appconfig";
import {
	evaluateCustomerType,
	flowSetting,
	getActiveVersion,
	getUserTimeZone,
	mapFeatureToggles
} from "../../libraries/utils";
import CoreEnvironmentHelper from "../../domains2/libraries/CoreEnvironmentHelper";
import MobileFriendlyCache from "../../libraries/MobileFriendlyCache";
import {applyActionForElements, applyActionForElementsItems, isGBOKioskCategoryModel} from "../../helper/CatalogHelper";
import i18n from "../../providers/localization/i18nProvider";
import get from "lodash/get";
import OrderHelperObject from "../../domains2/orders/OrderHelperObject";
import {NodeElements} from "../../domains2/products/ProductTypes";
import SectionHeaderElement from "../../domains2/products/SectionHeaderElement";
import GridBlockElement from "../../domains2/products/GridBlockElement";
import AppError from "../../domains2/libraries/AppError";
import {processGBOKioskCategoryElementsModel} from "../../helper/ProductCatalogHelpers";
import ProductCatalog from "../../domains2/products/ProductCatalog";
import {ProductMgr} from "../../domains2/products/ProductMgr";
import ProductTagsProvider from "../../providers/ProductTagsProvider";
import UpsellRulesProvider from "../../providers/UpsellRulesProvider";
import PriceRulesProvider from "../../providers/PriceRulesProvider";
import RecommendationEngineProvider from "../../providers/RecommendationEngineProvider";
import {cloneDeep} from "lodash";
import {getNonReactiveProductCatalog, setNonReactiveProductCatalog} from "../ProductCatalogNonReactiveStorage";
import { App as NativeApp } from "@capacitor/app";

const moduleCache = new MobileFriendlyCache();

NativeApp.addListener("appStateChange", (state) => {
	if (state.isActive) {
		// Check for inactivity-based disposal first (mobile needs this for fire-and-forget scenarios)
		if (!moduleCache.checkInactivityDisposal()) {
			// Only clean up expired items if cache wasn't disposed
			moduleCache.cleanupExpired();
		}
	}
});

const TimeBox = appConfig.timeBoxImpl.timebox;
const TimeBoxFormats = appConfig.timeBoxImpl.Formats;

export default ({
	enrichedProductCatalogData,
	priceRules,
	serverData,
	featureToggles,
	globalVersions
}) => {
	// initial state
	const state = {
		recomputeProductCatalog: false,
		serverData: Object.freeze(serverData),
		featureToggles: featureToggles,
		defaultFeatureToggles: featureToggles,
		globalError: null,
		globalErrorMessage: null,
		customGlobalErrorMessage: null,
		globalInfoDialog: null,
		redirectToLogin: false,
		redirectPath: null,
		enrichedProductCatalogData: Object.freeze(enrichedProductCatalogData),
		isFooterVisible: true,
		priceRules: priceRules && priceRules.filter(pr => {
			if(!pr.options || !pr.options.channels || !pr.options.channels.length) {
				return true;
			}
			return pr.options.channels.includes(appConfig.channelAlias());
		}),
		imageCDNRoot: serverData.imageCDNRoot,
		rootTenantId: serverData.rootTenantId,
		selectionExpiredMessage: false,
		version: null,
		globalVersions: globalVersions,
		activeVersion: getActiveVersion(globalVersions),
		upsellRulesData: null,
		productTagsData: [],
		productAssignedTagsData: [],
		// gigyaData: null,
		persistedData: {
			priceRules: [],
			rcmProductBadges: [],
			rcmProductBadgesFeedbackData: {
				recommendations: [],
				experimentId: null,
				picks: []
			},
			showLoaderPpdFlow: false
		},
		clientRedirects: [],
		skipInvalidBasketError: false
	};

	function getPrefix(env) {
		if (env && env.indexOf("-") > -1) {
			return env.split("-")[0];
		} else return "x";
	}

	const productCatalogCacheHelper = (state, getters) => {
		const selectedShopId = getters.globalSelectedShop && getters.globalSelectedShop.id ? getters.globalSelectedShop.id : getters.getShopData.id;
		const orderPickupDate = getters.getPickupDate;

		const key = [
			"productCatalogCache",
			selectedShopId ? selectedShopId : null,
			orderPickupDate ? orderPickupDate : null
		].filter(Boolean).join("-");

		const isShop = flowSetting.isFlow("shop");
		if (
			!moduleCache.get(key) ||
			!isShop
			|| (moduleCache.get(key) && (moduleCache.get(key).value) && !Object.keys((moduleCache.get(key).value).categoryMap).length)
		) {
			const pickupTimeZone = getters.pickupTimeZone;
			const currentDate = TimeBox.now()
				.convertTimezones(getUserTimeZone())
				.convertTimezones(pickupTimeZone)
				.format(TimeBoxFormats.YYYY_MM_DD).exec();

			if (!state.recomputeProductCatalog && isShop && !selectedShopId && CoreEnvironmentHelper.reducingNumbersOfProductCatalogCalls) {
				return undefined;
			}

			const enrichedProductCatalogData = getNonReactiveProductCatalog() || cloneDeep(state.enrichedProductCatalogData);

			const newProductCatalogData = !isShop
				? OrderHelperObject.getProductCatalogDataForEditingOrder(enrichedProductCatalogData, getters.selections)
				: enrichedProductCatalogData;

			const enrichedProductCatalog = (() => {
				if (appConfig.tenantConfig.isSGTenant) {
					// without the product catalog pipeline
					return ProductMgr.createEnrichedProductCatalog(
						newProductCatalogData,
						priceRules,
						selectedShopId,
						pickupTimeZone,
						getUserTimeZone(),
						currentDate,
						{ isAdmin: !isShop, orderPickupDate }
					);
				}

				return new ProductCatalog({
					categories: newProductCatalogData.categories,
					products: newProductCatalogData.products,
					departments: newProductCatalogData.departments,
					// We just pass through all the needed info so g4 can count the discounts
					priceRules: newProductCatalogData.priceRules,
					selectedShopId,
					pickupTimeZone,
					userTimeZone: getUserTimeZone(),
					currentDate,
					channel: appConfig.channelAlias(),
					deliveryMethod: getters.tenantSpecificData.deliveryChoice,
					customerType: evaluateCustomerType()
				});
			})();

			const mapperFunction = (mapper) => (category) => {
				if (isGBOKioskCategoryModel(category, appConfig.channelAlias())) {
					const deliveryChoiceId = getters.tenantSpecificData
						&& getters.tenantSpecificData.deliveryChoice !== undefined
						&& getters.tenantSpecificData.deliveryChoice !== null ? getters.tenantSpecificData.deliveryChoice : null;
					let elementsInGroupAmount = 3;
					if(featureToggles.moveTopMenuToTheLeftSide && featureToggles.newBasketPage && getters.globalSelectedShop.shopOptions.enableNewBasketPage && getters.globalSelectedShop.shopOptions.enableLeftMenuWithNewBasketDesignWithoutMarketingCards) {
						elementsInGroupAmount = 4;
					}
					const mappedElements = processGBOKioskCategoryElementsModel(category.elements, elementsInGroupAmount, enrichedProductCatalog, deliveryChoiceId, selectedShopId);
					category.isGBOCategory = true;
					category.elements = mappedElements.map((node) => {
						if (node.objType === "SectionHeaderElement") {
							return new SectionHeaderElement(node);
						} else if (node.objType === "GridBlockElement") {
							return new GridBlockElement(node, mapper);
						} else if (NodeElements.includes(node.objType)) {
							return node;
						} else {
							throw AppError.CodingError("Should never happen: ").addData(
								"node",
								node
							);
						}
					});
				}
			};

			// TODO: there is still mutation going on here, but this mapper function
			// might need to be applied inside the ProductCatalog constructor.
			// I just don't have the bandwidth to implement it properly without
			// making sure that I'm not breaking existing functionality.
			enrichedProductCatalog.categories.forEach(mapperFunction(enrichedProductCatalog.mapper));
			// this looks completely useless
			// enrichedProductCatalog.categoriesWithDisabled.forEach(mapperFunction(enrichedProductCatalog.mapperWithDisabled));

			moduleCache.put(
				key,
				enrichedProductCatalog
			);
		}

		const cacheResult = moduleCache.get(key);
		return cacheResult ? cacheResult.value : undefined;
	};

	// getters
	const getters = {
		serverData: state => state.serverData,
		redirectPath: state => state.redirectPath,
		tenantSettings: state => (typeof state.serverData === "object" && state.serverData) ? state.serverData.tenantSettings : undefined,
		rawFeatureToggles: state => state.featureToggles,
		defaultFeatureToggles: state => state.defaultFeatureToggles,
		featureToggles: state => mapFeatureToggles(state.featureToggles),
		azureADLoginConfig: state => state.serverData.azureADLoginConfig,
		appConfig: state => state.appConfig,
		globalError: state => state.globalError,
		globalErrorMessage: state => state.globalErrorMessage,
		selectionExpiredMessage: state => state.selectionExpiredMessage,
		customGlobalErrorMessage: state => state.customGlobalErrorMessage,
		globalInfoDialog: state => state.globalInfoDialog,
		envPrefix: state => getPrefix(state.serverData ? state.serverData.loadedEnvironment : null),
		redirectToLogin: state => state.redirectToLogin,
		pickupTimeZone: (state, getters) => {
			return getters.globalSelectedShop && getters.globalSelectedShop.shopTimeZone ? getters.globalSelectedShop.shopTimeZone : getters.serverData.defaultTimeZone;
		},
		productCatalog: (state, getters) => {
			return productCatalogCacheHelper(state, getters);
		},
		getNonCachedProductCatalog: (state, getters) => () => {
			return productCatalogCacheHelper(state, getters);
		},
		isFooterVisible: state => state.isFooterVisible,
		priceRules: state => state.persistedData.priceRules,
		imageCDNRoot: state => state.imageCDNRoot,
		rootTenantId: state => state.rootTenantId,
		channelAlias: () => appConfig.channelAlias(),
		productCatalogPure: (state) => getNonReactiveProductCatalog() || state.enrichedProductCatalogData,
		globalVersions: state => state.globalVersions,
		activeVersion: state => state.activeVersion,
		upsellRulesData: state => state.upsellRulesData,
		productTagsData: state => state.productTagsData,
		productAssignedTagsData: state => state.productAssignedTagsData,
		// gigyaData: (state) => state.gigyaData,
		rcmProductBadges: (state) => state.persistedData.rcmProductBadges,
		rcmProductBadgesFeedbackData: (state) => state.persistedData.rcmProductBadgesFeedbackData,
		clientRedirects: state => state.clientRedirects,
		showLoaderPpdFlow: state => state.persistedData.showLoaderPpdFlow,
		skipInvalidBasketError: state => state.skipInvalidBasketError
	};

	// actions
	const actions = {
		cacheServerData: ({commit, getters}, {forceRefresh = false}) => {
			if (getters.serverData && forceRefresh === false) {
				return Promise.resolve();
			} else {
				return Promise.all([
					ServerDataProvider.getServerData(!forceRefresh && getters && getters.featureToggles && getters.featureToggles.useApiCdnCaching),
					FeatureToggleProvider.getFeatureTogglesAsPromise(forceRefresh)
				]).then(([serverData, featureToggles]) => {
					commit(types.SET_SERVER_DATA, serverData);
					commit(types.SET_FEATURE_TOGGLES, featureToggles);
					if(window.parent) window.parent.postMessage({ type: "SERVER_DATA", payload: { ...serverData, featureToggles } }, "*");
				}).catch(() => {
					commit(types.SET_SERVER_DATA, {});
				});
			}
		},
		[types.ADD_GLOBAL_ERROR]: ({commit, getters}, error) => {
			ServerLogProvider.postErrorToServer(error).then(() => {
				if (error && (error.errorObject || {}).errorCode === "ProductIsInactive") {
					const useNewProductCatalog = getters.featureToggles.useNewProductCatalog || false;
					ProductCatalogProvider.getProductCatalog(null, useNewProductCatalog).then(productCatalog => {
						const catalogProducts = getters.cartProducts.map(product => productCatalog.getProduct(product.productId, true));
						const inactiveProducts = catalogProducts.filter(product => !product.isActive);
						error.customMessage = inactiveProducts.map(product => product.name).join(",");
						commit(types.ADD_GLOBAL_ERROR, error);
					});
				} else {
					commit(types.ADD_GLOBAL_ERROR, error);
				}
			});
		},
		[types.ADD_SELECTION_EXPIRED_MESSAGE]: ({commit}) => {
			commit(types.ADD_SELECTION_EXPIRED_MESSAGE);
		},
		[types.CLEAR_SELECTION_EXPIRED_MESSAGE]: ({commit}) => {
			commit(types.CLEAR_SELECTION_EXPIRED_MESSAGE);
		},
		[types.CLEAR_GLOBAL_ERRORS]: ({commit}) => {
			commit(types.CLEAR_GLOBAL_ERRORS);
		},
		[types.CLEAR_REDIRECT_TO_LOGIN]: ({commit}) => {
			commit(types.CLEAR_REDIRECT_TO_LOGIN);
		},
		[types.CHANGE_FOOTER_VISIBILITY]: ({commit}, value) => {
			return commit(types.CHANGE_FOOTER_VISIBILITY, value);
		},
		[types.CHANGE_PRODUCT_CATALOG_ELEMENTS]: ({commit}, value) => {
			return commit(types.CHANGE_PRODUCT_CATALOG_ELEMENTS, value);
		},
		// products
		createProduct: async ({dispatch, getters}, productData) => {
			const productResult = await ProductCatalogProvider.createProduct(productData, getters.jwtToken);

			// update vuex store with new product
			await dispatch("refreshProductCatalog");

			return productResult;
		},
		// Duplicate product and return the new product
		duplicateProduct: async ({ dispatch, getters }, { productId, versionId }) => {
			const productResult = await ProductCatalogProvider.duplicateProduct(productId, getters.jwtToken, versionId);

			// update vuex store with new product
			await dispatch("refreshProductCatalog");

			return productResult;
		},
		[types.SHOW_DYNAMIC_LOCALIZATION_DISCLAIMER]: ({ getters, commit }) => {
			// TODO: maybe clearing the cache can also be done here.
		// show disclaimer if dynamic translations are enabled.
			const isLocalizationEnabled = typeof getters.featureToggles.dynamicLocalization !== "undefined"
				? getters.featureToggles.dynamicLocalization
				: appConfig.tenantConfig.enableDynamicLocalization;

			if(isLocalizationEnabled) {
				commit(types.SET_GLOBAL_INFO_DIALOG, "admin.tenantSettings.tenantSettingsMain.dynamicTranslationDisclaimer");
			}
		},
		updateProduct: ({dispatch, getters}, product) => {
			return ProductCatalogProvider.updateProduct(product, getters.jwtToken).then(() => {
				dispatch(types.SHOW_DYNAMIC_LOCALIZATION_DISCLAIMER);

				// update vuex store with new product
				return dispatch("refreshProductCatalog");
			});
		},

		updateProductImages: ({dispatch, getters}, product) => {
			return ProductCatalogProvider.updateProductImages(product, getters.jwtToken).then(() => {
				// update vuex store with new product
				return dispatch("refreshProductCatalog");
			});
		},

		removeProduct: ({dispatch, getters}, productId) => {
			return ProductCatalogProvider.removeProduct(productId, getters.jwtToken).then(() => {
				// update vuex store with new product
				return dispatch("refreshProductCatalog");
			});
		},

		addCatalog: async ({dispatch, getters}, catalog) => {
			const newCatalogObject = await ProductCatalogProvider.addCatalog(catalog, getters.jwtToken);
			// update vuex store with new catalog
			await dispatch("refreshProductCatalog");

			return newCatalogObject;
		},

		updateCatalog: async ({ dispatch, getters }, catalog) => {
			moduleCache.remove("productCatalogCached");
			await ProductCatalogProvider.updateCatalog(catalog, getters.jwtToken);

			dispatch(types.SHOW_DYNAMIC_LOCALIZATION_DISCLAIMER);

			// update vuex store with new catalog
			return dispatch("refreshProductCatalog");
		},

		removeCatalog: async ({dispatch, getters}, data) => {
			moduleCache.remove("productCatalogCached");
			await ProductCatalogProvider.removeCatalog(data.deletingCatalogId, data.versionId, getters.jwtToken);
			// update vuex store with new catalog
			return dispatch("refreshProductCatalog");
		},

		async insertProductGroup({dispatch, getters}, groupData) {
			await ProductCatalogProvider.insertProductGroup(groupData, getters.jwtToken, getters.featureToggles.useNewProductCatalog);

			return dispatch("refreshProductCatalog");
		},

		async getAllProductGroups({dispatch, getters}) {
			await dispatch("refreshProductCatalog");
			return ProductCatalogProvider.getAllProductGroups(getters.jwtToken, getters.featureToggles.useNewProductCatalog);
		},

		async updateProductGroup({dispatch, getters}, {groupId, groupData}) {
			await ProductCatalogProvider.updateProductGroup({groupId, groupData}, getters.jwtToken, getters.featureToggles.useNewProductCatalog);

			return dispatch("refreshProductCatalog");
		},

		async toggleProductGroupDisabledState({dispatch, getters}, {groupId, versionId, disabledAt}) {
			await ProductCatalogProvider.toggleProductGroupDisabledState(
				{groupId, versionId, disabledAt},
				getters.jwtToken,
				getters.featureToggles.useNewProductCatalog
			);

			return dispatch("refreshProductCatalog");
		},

		async removeProductGroup({dispatch, getters}, {groupId, versionId}) {
			await ProductCatalogProvider.removeProductGroup(groupId, versionId, getters.jwtToken);

			return dispatch("refreshProductCatalog");
		},

		async insertProductConfiguration({dispatch, getters}, configData) {
			await ProductCatalogProvider.insertProductConfiguration(configData, getters.jwtToken, getters.featureToggles.useNewProductCatalog);

			return dispatch("refreshProductCatalog");
		},

		async updateProductConfiguration({dispatch, getters}, {configId, configData}) {
			await ProductCatalogProvider.updateProductConfiguration({configId, configData}, getters.jwtToken, getters.featureToggles.useNewProductCatalog);

			return dispatch("refreshProductCatalog");
		},

		async toggleProductConfigurationDisabledState({dispatch, getters}, {configId, versionId, disabledAt}) {
			await ProductCatalogProvider.toggleProductConfigurationDisabledState(
				{configId, versionId, disabledAt},
				getters.jwtToken,
				getters.featureToggles.useNewProductCatalog
			);

			return dispatch("refreshProductCatalog");
		},

		async removeProductConfiguration({dispatch, getters}, {configId, versionId}) {
			await ProductCatalogProvider.removeProductConfiguration(configId, versionId, getters.jwtToken, getters.featureToggles.useNewProductCatalog);

			return dispatch("refreshProductCatalog");
		},

		async updateAssortmentRulesByProduct({dispatch, getters}, {productId, shopIds}) {
			await ProductCatalogProvider.updateAssortmentRulesByProduct({
				productId,
				shopIds
			}, getters.jwtToken, getters.featureToggles.useNewProductCatalog);

			return dispatch("refreshProductCatalog");
		},

		refreshProductCatalog({getters, commit}, args = null) {
			const useApiCdnCachingFT = getters.featureToggles.useApiCdnCaching;
			const useNewProductCatalogFT = getters.featureToggles.useNewProductCatalog;
			const useNestOrderPaymentFlow = getters.featureToggles.useNestOrderPaymentFlow;
			const globalSelectedShopId = getters.globalSelectedShop ? getters.globalSelectedShop.id : null;
			const deliveryMethod = (args && (args.deliveryMethod !== null && args.deliveryMethod !== undefined)) ? args.deliveryMethod : null;
			const invalidateCache = (args && args.invalidateCache) ? args.invalidateCache : false;

			return ProductCatalogProvider
				.getProductCatalogData({ useNewProductCatalog: useNewProductCatalogFT, useApiCdnCaching: useApiCdnCachingFT, shopId: globalSelectedShopId, deliveryMethod, useNestOrderPaymentFlow })
				.then(productCatalogData => {
					if (invalidateCache) {
						const selectedShopId = getters.globalSelectedShop && getters.globalSelectedShop.id ? getters.globalSelectedShop.id : getters.getShopData.id;
						const orderPickupDate = getters.getPickupDate;
						const key = [
							"productCatalogCache",
							selectedShopId ? selectedShopId : null,
							orderPickupDate ? orderPickupDate : null
						].filter(Boolean).join("-");
						moduleCache.remove(key);
					}
					if(flowSetting.isFlow("admin")) {
						commit(types.REFRESH_PRODUCT_CATALOG, productCatalogData);
					} else {
						setNonReactiveProductCatalog(productCatalogData);
					}
					productCatalogData.priceRules && commit(types.SET_PRICE_RULES_DATA, Object.freeze(productCatalogData.priceRules));
					// if this function doesn't return anything, promise will be immediately resolved and await doesn't have any effect
					return {
						productCatalog: productCatalogCacheHelper(state, getters)
					};
				});
		},

		async updateServerData({dispatch, getters}, {settingName, settingValue, useBifrostNest = false}) {
			const updateData = await ServerDataProvider.setServerData({
				settingName,
				settingValue,
				useBifrostNest,
				jwtToken: getters.jwtToken
			});
			await dispatch("cacheServerData", {
				forceRefresh: true
			});

			return updateData;
		},

		async updateMassProducts({dispatch, getters}, data) {
			await ProductCatalogProvider.updateMassProducts(data, getters.jwtToken);
			return dispatch("refreshProductCatalog");
		},

		[types.FETCH_PRODUCT_TAGS]: async ({ getters, commit }) => {
			const useApiCdnCaching = getters.featureToggles.useApiCdnCaching;
			const [productTags, productAssignedTags] = await (async () => {
				const useProductTags = getters.featureToggles.productTagsFilter;
				if (!useProductTags) {
					return [ { data: [] }, { data: [] } ];
				}

				return Promise.all([
					ProductTagsProvider.getProductTags(useApiCdnCaching),
					ProductTagsProvider.getProductAssignedTags(useApiCdnCaching)
				]);
			})();

			commit(types.SET_PRODUCT_TAGS_DATA, productTags.data);
			commit(types.SET_PRODUCT_ASSIGNED_TAGS_DATA, productAssignedTags.data);
		},
		[types.FETCH_UPSELL_RULES]: async ({ getters, commit }) => {
			const [upsellRules] = await (async () => {
				const crossSellRules = getters.featureToggles.crossSellRules;
				if (!crossSellRules) {
					return [ { data: [] }, { data: [] } ];
				}

				return Promise.all([
					UpsellRulesProvider.findAll({}, getters.featureToggles.useApiCdnCaching)
				]);
			})();
			commit(types.SET_UPSELL_RULES_DATA, upsellRules.data);
		},
		[types.FETCH_PRICE_RULES]: async ({ getters, commit }) => {
			const [priceRules] = await (async () => {
				return Promise.all([
					PriceRulesProvider.findAll({}, getters.featureToggles.useApiCdnCaching)
				]);
			})();
			commit(types.SET_PRICE_RULES_DATA, priceRules.data);
		},
		[types.FETCH_RCM_PRODUCT_BADGES]: ({ commit, getters }) => {
			if (!getters.featureToggles.enableRcmProductBadges) return;
			RecommendationEngineProvider.obtainBadges(getters.featureToggles.useApiCdnCaching)
				.then((data) => commit(types.SET_RCM_PRODUCT_BADGES, data));
		}
	};

	const KNOWN_ERRORS = {
		OrderCustomerDetailsUndefinedError: {
			key: "knownErrors.orderCustomerDetailsUndefinedError",
			defaultValue: "Kundedata udefineret"
		},
		CapacityRulesInvalidInput: {
			key: "knownErrors.invalidInput",
			defaultValue: "Invalid input"
		},
		CapacityRulesDateWeekdayUndefined: {
			key: "knownErrors.capacityRulesDateWeekdayUndefined",
			defaultValue: "Date or weekday must be selected"
		},
		CapacityRulesLimitUndefined: {
			key: "knownErrors.capacityRulesLimitUndefined",
			defaultValue: "Limit must be selected"
		},
		CapacityRulesShopUndefined: {
			key: "knownErrors.shopUndefined",
			defaultValue: "Du mangler at vælge udleveringsbutik."
		},
		CapacityRulesDepartmentUndefined: {
			key: "knownErrors.capacityRulesDepartmentUndefined",
			defaultValue: "Department undefined"
		},
		CapacityRulesProductUndefined: {
			key: "knownErrors.capacityRulesProductUndefined",
			defaultValue: "Product undefined"
		},
		PunchCardNumberOfPunches: {
			key: "knownErrors.punchCardNumberOfPunches",
			defaultValue: "Punch card rule must contain number of punches required for gift"
		},
		PriceRulesShopUndefined: {
			key: "knownErrors.shopUndefined",
			defaultValue: "Du mangler at vælge udleveringsbutik."
		},
		PriceRulesProductError: {
			key: "knownErrors.priceRulesProductError",
			defaultValue: "You need to select a product, or check the 'Not product specific' checkbox"
		},
		PickupRulesRequiredFields: {
			key: "knownErrors.pickupRulesRequiredFields",
			defaultValue: "All required fields must have values"
		},
		PickupRulesInvalidInput : {
			key: "knownErrors.invalidInput",
			defaultValue: "Invalid input"
		},
		PickupRulesTimeIntervalsMissed : {
			key: "knownErrors.pickupRulesTimeIntervalsMissed",
			defaultValue: "Time rule must contain time intervals"
		},
		PickupRulesDatesMissed : {
			key: "knownErrors.pickupRulesDatesMissed",
			defaultValue: "Date rule must contain date intervals"
		},
		PickupRulesShopUndefined : {
			key: "knownErrors.shopUndefined",
			defaultValue: "Du mangler at vælge udleveringsbutik."
		},
		PickupRulesProductUndefined : {
			key: "knownErrors.pickupRulesProductUndefined",
			defaultValue: "Product undefined"
		},
		PickupRulesDuplicateRule: {
			key: "knownErrors.pickupRulesDuplicateRule",
			defaultValue: "A rule with the same values already exists"
		},
		ProductInvalidEan: {
			key: "knownErrors.productInvalidEan",
			defaultValue: "Dette er ikke et gyldigt EAN nummer"
		},
		TwoForOneOddProductNumberError: {
			key: "knownErrors.twoForOneOddProductNumberError",
			defaultValue: "Dette valg består som mimimum af to burgere - du betaler kun for én."
		},
		OrderCustomerNameUndefinedError: {
			key: "knownErrors.orderCustomerNameUndefinedError",
			defaultValue: "Du mangler at indtaste et navn"
		},
		OrderCustomerEmailUndefinedError: {
			key: "knownErrors.orderCustomerEmailUndefinedError",
			defaultValue: "Ugyldig email"
		},
		OrderCustomerDataUndefinedError: {
			key: "knownErrors.orderCustomerDataUndefinedError",
			defaultValue: "Du skal enten udfylde navn, email eller telefonnummer for at oprette ordren."
		},
		OrderCustomerTelUndefinedError: {
			key: "knownErrors.orderCustomerTelUndefinedError",
			defaultValue: "Du mangler at indtaste et telefonnummer"
		},
		OrderShopUndefinedError: {
			key: "knownErrors.shopUndefined",
			defaultValue: "Du mangler at vælge udleveringsbutik."
		},
		ShopUndefinedError: {
			key: "knownErrors.shopUndefined",
			defaultValue: "Du mangler at vælge udleveringsbutik."
		},
		OrderPickupTimeUndefinedError: {
			key: "knownErrors.orderPickupTimeUndefinedError",
			defaultValue: "Du skal vælge leveringstidspunktet"
		},
		OrderPickupTimeInvalidError: {
			key: "knownErrors.orderPickupTimeInvalidError",
			defaultValue: "Du kan ikke bestille på dette tidspunkt"
		},
		OrderEmptyError: {
			key: "knownErrors.orderEmptyError",
			defaultValue: "Ordren må ikke være tom"
		},
		OrderPickupDateUndefinedError: {
			key: "knownErrors.orderPickupDateUndefinedError",
			defaultValue: "Du mangler at vælge udleveringsdato"
		},
		OrderPickupDateInvalidError: {
			key: "knownErrors.orderPickupDateInvalidError",
			defaultValue: "Du har valgt en ugyldig udleveringsdato, enten fordi den givne vare ikke sælges på den ønskede dag eller fordi bestillingsfristen ikke er overholdt."
		},
		OrderPickupDateInvalidErrorForBM100: {
			key: "knownErrors.orderPickupDateInvalidErrorForBM100",
			defaultValue: "Du har varer i kurven, som ikke kan sælges."
		},
		ProductCannotBeHeatedError: {
			key: "knownErrors.productCannotBeHeatedError",
			defaultValue: "This product cannot be heated!!!"
		},
		ProductIsInactive: {
			key: "knownErrors.productIsInactive",
			defaultValue: "kan ikke længere købes"
		},
		OrderCapacityLimitReachedError: {
			key: "knownErrors.",
			defaultValue: "Kutikken har desværre ikke mulighed for at modtage din ordre grundet kapacitetsproblemer.orderCapacityLimitReachedError"
		},
		InvalidProductDeleteError: {
			key: "knownErrors.invalidProductDeleteError",
			defaultValue: "Only products that are not root, and not listed on admin and not in pending orders can be deleted"
		},
		ResendEmailFailedError: {
			key: "knownErrors.resendEmailFailedError",
			defaultValue: "Email re-sending failed"
		},
		UpsellRulesInvalidInput : {
			key: "knownErrors.invalidInput",
			defaultValue: "Invalid input"
		},
		UpsellRulesShopUndefined: {
			key: "knownErrors.shopUndefined",
			defaultValue: "Du mangler at vælge udleveringsbutik."
		},
		TermsAndConditionsInput : {
			key: "knownErrors.invalidInput",
			defaultValue: "nvalid input"
		},
		PriceRulesVoucherExistsRule: {
			key: "knownErrors.priceRulesVoucherExistsRule",
			defaultValue: "Denne voucher er allerede implementeret på en anden prisregel."
		},
		VouchersOnlinePosProductIdExistsRule: {
			key: "knownErrors.vouchersOnlinePosProductIdExistsRule",
			defaultValue: "Online POS-produkt-id findes allerede."
		},
		InsufficientSunsetPointsError: {
			key: "knownErrors.insufficientSunsetPointsError",
			defaultValue: "Ikke nok point til din ordrerabat. Tjek venligst din SunsetPOINT-rabat igen i kurven."
		},
		InvalidDataError: (responseData) => {
			if (responseData.validationResult) {
				return Object.values(responseData.validationResult).map(value => value[0]).join("; ");
			}

			return "Invalid data submitted";
		},
		InvalidDeliveryChoice: {
			key: "knownErrors.invalidDeliveryChoice",
			defaultValue: "Den valgte leveringsmetode er ikke tilgængelig for den valgte butik."
		}
	};

	function translate(defaultValue, key) {
		if(i18n) {
			const value = get(i18n.messages[i18n.locale], key, defaultValue);
			if(value) {
				return value;
			}
		}
		return defaultValue;
	}

	// mutations
	const mutations = {
		[types.SET_SERVER_DATA](state, serverData) {
			state.serverData = serverData;
		},
		[types.SET_REDIRECT_PATH]: (state, redirectPath) => {
			state.redirectPath = redirectPath;
		},
		[types.SET_FEATURE_TOGGLES](state, featureToggles) {
			state.featureToggles = featureToggles;
			state.defaultFeatureToggles = featureToggles;
		},
		[types.SET_AB_TEST_FEATURE_TOGGLES](state, featureToggles) {
			state.featureToggles = featureToggles;
		},

		[types.ADD_GLOBAL_ERROR](state, error) {
			let foundUserFriendlyError = false;
			let msg = undefined;
			const errorCode = error.errorCode || error.errorName;
			if (errorCode) {
				if (errorCode && KNOWN_ERRORS[errorCode] !== undefined) {
					//We will only show known errors to customers.
					if (typeof KNOWN_ERRORS[errorCode] === "function") {
						msg = KNOWN_ERRORS[errorCode](error.data);
					} else {
						const { key, defaultValue } = KNOWN_ERRORS[errorCode];
						msg = translate(defaultValue, key);
					}

					foundUserFriendlyError = true;
				} else {
					msg = errorCode;
					const traceId = error.traceId || error.errorTraceId;
					if (traceId) {
						msg += ` (fejl-ID: ${traceId})`;
					}
				}
			} else if (error.friendlyMessage) {
				msg = error.friendlyMessage;
				foundUserFriendlyError = true;
			} else if (error.errors && Object.keys(error.errors).length) {
				msg = Object.values(error.errors).join("\n");
			} else if (error.message) {
				msg = error.message;
			}

			if (msg === undefined) msg = "Der skete en uventet fejl. Prøv venligst igen.";

			if (foundUserFriendlyError) {
				state.globalErrorMessage = msg;
				state.globalError = error;
				if(error.errorTextUserFriendly) {
					state.customGlobalErrorMessage = error.errorTextUserFriendly;
				} else if(error.customMessage) {
					state.customGlobalErrorMessage = error.customMessage + " " + msg;
				}
			}

			if (error.code === 401) {
				state.redirectToLogin = true;
			}
			if (error.redirectPath) {
				state.redirectPath = error.redirectPath;
			}

		},
		[types.RECOMPUTE_PRODUCT_CATALOG_CACHE]: (state, val) => {
			state.recomputeProductCatalog = val;
		},
		[types.ADD_SELECTION_EXPIRED_MESSAGE](state) {
			state.selectionExpiredMessage = true;
		},
		[types.CLEAR_SELECTION_EXPIRED_MESSAGE](state) {
			state.selectionExpiredMessage = false;
		},
		[types.CLEAR_GLOBAL_ERRORS](state) {
			state.globalError = null;
			state.globalErrorMessage = null;
			state.customGlobalErrorMessage = null;
		},
		[types.SET_GLOBAL_INFO_DIALOG](state, payload) {
			state.globalInfoDialog = payload; // Payload is expected to be translation key, not a plain string;
		},
		[types.CLEAR_REDIRECT_TO_LOGIN](state) {
			state.redirectToLogin = false;
		},
		[types.REFRESH_PRODUCT_CATALOG](state, enrichedProductCatalogData) {
			state.enrichedProductCatalogData = enrichedProductCatalogData;
		},
		[types.CHANGE_FOOTER_VISIBILITY](state, value) {
			state.isFooterVisible = value;
		},
		[types.CHANGE_PRODUCT_CATALOG_ELEMENTS](state, value) {
			if (!("selectedCatalog" in value && "action" in value && "actionData" in value)) {
				return;
			}
			if (value.isSection || value.isMenu) {
				applyActionForElements(value.selectedCatalog, value.action, value.actionData);
			} else {
				applyActionForElementsItems(value.selectedCatalog, value.action, value.actionData);
			}
		},
		[types.SET_PRODUCT_TAGS_DATA](state, productTagsData) {
			state.productTagsData = productTagsData;
		},
		[types.SET_PRODUCT_ASSIGNED_TAGS_DATA](state, productAssignedTagsData) {
			state.productAssignedTagsData = productAssignedTagsData;
		},
		[types.SET_UPSELL_RULES_DATA](state, upsellRulesData) {
			state.upsellRulesData = upsellRulesData;
		},
		[types.SET_PRICE_RULES_DATA](state, priceRules) {
			state.persistedData.priceRules = priceRules;
		},
		[types.SET_RCM_PRODUCT_BADGES](state, data) {
			state.persistedData.rcmProductBadges = data;
		},
		[types.SET_RCM_PRODUCT_BADGES_RECOMMENDATIONS]: (state, data) => {
			state.persistedData.rcmProductBadgesFeedbackData.recommendations = [...data.recommendations];
			state.persistedData.rcmProductBadgesFeedbackData.experimentId = data.experimentId;
		},
		[types.SET_RCM_PRODUCT_BADGES_PICKS]: (state, pickedItem = null) => {
			if(!pickedItem) {
				state.persistedData.rcmProductBadgesFeedbackData.picks = [];
				return;
			}
			state.persistedData.rcmProductBadgesFeedbackData.picks = [
				...state.persistedData.rcmProductBadgesFeedbackData.picks,
				pickedItem
			];
		},
		[types.SET_CLIENT_REDIRECTS_DATA](state, clientRedirects) {
			state.clientRedirects = clientRedirects;
		},
		[types.SHOW_LOADER_AFTER_PPD_CATALOG_CREATION] (state, showLoaderPpdFlow) {
			state.persistedData.showLoaderPpdFlow = showLoaderPpdFlow;
		},
		[types.SKIP_INVALID_BASKET_ERROR] (state, payload) {
			state.skipInvalidBasketError = payload;
		}
	};

	return {
		state,
		getters,
		actions,
		mutations
	};

};
